import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { observer } from "mobx-react-lite"

import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { makeBreadcrumbs } from "@/domains/lock-page/breadcrumbs"
import { VenammCard } from "@/domains/lock-page/components/card/venamm-card/components/venamm-card"
import { LockPageLayout } from "@/domains/lock-page/components/lock-page-layout"
import { LockPageTabs } from "@/domains/lock-page/components/lock-page-tabs"
import { MANAGE_LOCK_TABS } from "@/domains/lock-page/constants"
import { ComplexTab } from "@/domains/navigation/components/complex-breadcrumb-dropdown"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useNavigateOnLockCleared } from "@/hooks/use-navigate-on-lock-cleared"
import { useSyncLockIdFromRouter } from "@/hooks/use-sync-lock-id-from-router"
import { useVestedToken } from "@/hooks/use-vested-token"

export default observer(() => {
  const navigate = useNavigate()

  const governanceToken = useGovernanceToken()
  const vestedToken = useVestedToken()
  const scaleApp = useScaleApplication()
  const breadcrumbs = makeBreadcrumbs()

  useSyncLockIdFromRouter()
  useNavigateOnLockCleared()

  const selectedLock = scaleApp.Pages.Lock.selectedLock
  const orchestrator = scaleApp.Venamm.Sell.Orchestrator
  const venammStore = scaleApp.Venamm.Store
  const quoteService = scaleApp.LockVenammQuotes.Service
  const quoteCache = scaleApp.LockVenammQuotes.Cache

  // Update orchestrator when selected lock changes
  useEffect(() => {
    orchestrator.updateLock(selectedLock)
  }, [selectedLock?.id, orchestrator])

  // Fetch quote when lock is selected
  useEffect(() => {
    if (selectedLock) {
      const cachedQuote = quoteCache.getQuote(selectedLock.id)
      // Fetch if no cache or cache is stale (> 60 seconds)
      if (!cachedQuote || Date.now() - cachedQuote.fetchedAt > 60_000) {
        quoteService.fetchQuoteForLock(selectedLock.id)
      }
    }
  }, [selectedLock?.id, quoteCache, quoteService])

  // Get cached quote for display
  const quote = selectedLock ? quoteCache.getQuote(selectedLock.id) : null

  // Handle successful sell - navigate away
  useEffect(() => {
    if (orchestrator.store.state === "complete") {
      const timer = setTimeout(() => {
        navigate("/lock")
      }, 10000)
      return () => clearTimeout(timer)
    }
  }, [orchestrator.store.state, navigate])

  const complexTab: ComplexTab | undefined = MANAGE_LOCK_TABS[6]

  if (!complexTab) {
    return null
  }

  const title = `Venamm`

  return (
    <PageContainer>
      <MetaData title={title} />
      <PageTitle title={title} items={breadcrumbs} />
      <TooltipProvider>
        <LockPageLayout>
          <LockPageTabs />
          <VenammCard
            store={orchestrator.store}
            governanceToken={governanceToken}
            vestedToken={vestedToken}
            venammStore={venammStore}
            icon={complexTab.icon ? <complexTab.icon /> : undefined}
            onCancel={() => {
              orchestrator.cancel()
            }}
            onStartSell={() => {
              orchestrator.startSellFlow()
            }}
            onReset={() => {
              orchestrator.executeReset()
            }}
            onRetryReset={() => {
              orchestrator.retryReset()
            }}
            onRetryQuote={() => {
              orchestrator.retryQuote()
            }}
            onApprove={() => {
              orchestrator.executeApprove()
            }}
            onRetryApprove={() => {
              orchestrator.retryApprove()
            }}
            onSell={() => {
              orchestrator.executeSell()
            }}
            onRetrySell={() => {
              orchestrator.retrySell()
            }}
          />
        </LockPageLayout>
      </TooltipProvider>
    </PageContainer>
  )
})
